import {
  memo, useEffect, useRef, useState,
} from '../../lib/teact/teact';

import type { Operator } from '../../services/crmStorage';

import buildClassName from '../../util/buildClassName';
import {
  fetchOperatorsApi,
  getActiveOperator,
  OPERATORS,
  setActiveOperator,
  verifyPin,
} from '../../services/crmStorage';
import {
  closeOperatorModal,
  getIsOperatorModalOpen,
} from '../../services/crmViewsState';

import useDerivedState from '../../hooks/useDerivedState';
import useLastCallback from '../../hooks/useLastCallback';

import Icon from '../common/icons/Icon';

import styles from './OperatorAuthModal.module.scss';

interface OwnProps {
  isOpen?: boolean;
  onClose?: NoneToVoidFunction;
}

import { CRM_BACKEND_URL as BACKEND_URL } from '../../util/crmBackendUrl';

const OPERATORS_STORAGE_KEY = 'telegram_crm_operators_v1';
const MAX_ATTEMPTS = 5;
const LOCKOUT_SECONDS = 30;

const AVATAR_COLORS = [
  '#0284c7', '#7c3aed', '#059669', '#dc2626',
  '#d97706', '#db2777', '#0891b2', '#65a30d',
];

function getAvatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function loadOperators(): Operator[] {
  try {
    const raw = localStorage.getItem(OPERATORS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Operator[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // Fallback al array predeterminado
  }
  return OPERATORS;
}

const OperatorAuthModal = ({ isOpen, onClose }: OwnProps) => {
  const isSignalOpen = useDerivedState(getIsOperatorModalOpen);
  const isVisible = isOpen !== undefined ? isOpen : isSignalOpen;
  const handleClose = onClose || closeOperatorModal;

  const [operators, setOperators] = useState<Operator[]>(loadOperators);
  const [selectedOpId, setSelectedOpId] = useState<string>(() => {
    const active = getActiveOperator();
    return active?.id || 'op_luis';
  });
  const [pinDigits, setPinDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [pinError, setPinError] = useState<string | undefined>(undefined);
  const [feedback, setFeedback] = useState<string | undefined>(undefined);
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | undefined>(undefined);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const pinInputsRef = useRef<Array<HTMLInputElement | undefined>>([]);

  // Recargar operadores cuando cambian en el sistema
  useEffect(() => {
    const handleOperatorsChanged = (e: CustomEvent<Operator[]>) => {
      if (e.detail && Array.isArray(e.detail)) {
        setOperators(e.detail);
      }
    };
    window.addEventListener('crm:operators-changed', handleOperatorsChanged as EventListener);
    return () => {
      window.removeEventListener('crm:operators-changed', handleOperatorsChanged as EventListener);
    };
  }, []);

  // Temporizador de cuenta regresiva de bloqueo
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const interval = window.setInterval(() => {
      const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockedUntil(undefined);
        setAttempts(0);
        setPinError(undefined);
        setSecondsLeft(0);
      } else {
        setSecondsLeft(remaining);
      }
    }, 500);
    return () => window.clearInterval(interval);
  }, [lockedUntil]);

  // Al abrir el modal, refrescar lista de operadores y reiniciar campos
  useEffect(() => {
    if (isVisible) {
      const currentOps = loadOperators();
      setOperators(currentOps);
      fetchOperatorsApi().then((remoteOps) => {
        if (remoteOps && remoteOps.length > 0) {
          setOperators(remoteOps);
        }
      });
      const active = getActiveOperator();
      const exists = currentOps.some((op) => op.id === active.id);
      setSelectedOpId(exists ? active.id : (currentOps[0]?.id || 'op_luis'));
      setPinDigits(['', '', '', '', '', '']);
      setPinError(undefined);
      setFeedback(undefined);
      window.setTimeout(() => {
        pinInputsRef.current[0]?.focus();
      }, 150);
    }
  }, [isVisible]);

  const selectedOperator = operators.find((op) => op.id === selectedOpId)
    || operators[0]
    || { id: 'op_luis', name: 'Operador', role: 'Operador' };

  const isLocked = secondsLeft > 0;

  const executeAuth = useLastCallback(async (enteredPin: string) => {
    if (isLocked) return;

    if (selectedOperator.pin) {
      const isValid = await verifyPin(enteredPin, selectedOperator.pin);
      if (!isValid) {
        const nextAttempts = attempts + 1;
        setAttempts(nextAttempts);
        if (nextAttempts >= MAX_ATTEMPTS) {
          const until = Date.now() + LOCKOUT_SECONDS * 1000;
          setLockedUntil(until);
          setSecondsLeft(LOCKOUT_SECONDS);
          setPinError(`Demasiados intentos fallidos. Bloqueado temporalmente.`);
        } else {
          setPinError(`PIN incorrecto (intento ${nextAttempts} de ${MAX_ATTEMPTS})`);
        }
        setPinDigits(['', '', '', '', '', '']);
        pinInputsRef.current[0]?.focus();
        return;
      }
    }

    try {
      await fetch(`${BACKEND_URL}/auth/verify-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operator_id: selectedOpId,
          operator_name: selectedOperator.name,
        }),
      });
    } catch {
      // Fallback local
    }

    setActiveOperator(selectedOpId);
    setAttempts(0);
    setPinError(undefined);
    setFeedback(`✅ Turno asignado a ${selectedOperator.name}`);
    window.setTimeout(() => {
      setFeedback(undefined);
      setPinDigits(['', '', '', '', '', '']);
      handleClose();
    }, 700);
  });

  const handlePinDigitChange = useLastCallback((index: number, val: string) => {
    if (isLocked) return;
    const sanitized = val.replace(/\D/g, '');

    // Manejo de pegado (paste) de múltiples dígitos
    if (sanitized.length > 1) {
      const digits = sanitized.slice(0, 6).split('');
      const updated = [...pinDigits];
      for (let j = 0; j < 6; j++) {
        if (digits[j] !== undefined) {
          updated[j] = digits[j];
        }
      }
      setPinDigits(updated);
      setPinError(undefined);
      const nextIdx = Math.min(digits.length, 5);
      pinInputsRef.current[nextIdx]?.focus();
      if (digits.length === 6) {
        executeAuth(digits.join(''));
      }
      return;
    }

    const digit = sanitized.slice(-1);
    const updated = [...pinDigits];
    updated[index] = digit;
    setPinDigits(updated);
    setPinError(undefined);

    if (digit && index < 5) {
      pinInputsRef.current[index + 1]?.focus();
    }

    if (digit && index === 5) {
      const full = updated.join('');
      if (full.length === 6) {
        executeAuth(full);
      }
    }
  });

  const handlePinKeyDown = useLastCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!pinDigits[index] && index > 0) {
        pinInputsRef.current[index - 1]?.focus();
        const updated = [...pinDigits];
        updated[index - 1] = '';
        setPinDigits(updated);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      pinInputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      pinInputsRef.current[index + 1]?.focus();
    }
  });

  const handleSelectOperator = useLastCallback((opId: string) => {
    setSelectedOpId(opId);
    setPinDigits(['', '', '', '', '', '']);
    setPinError(undefined);
    window.setTimeout(() => {
      pinInputsRef.current[0]?.focus();
    }, 50);
  });

  const handleConfirm = useLastCallback(() => {
    if (!selectedOperator.pin) {
      executeAuth('');
      return;
    }
    const fullPin = pinDigits.join('');
    if (fullPin.length === 6) {
      executeAuth(fullPin);
    } else {
      setPinError('Ingresa los 6 dígitos de tu PIN de seguridad');
    }
  });

  if (!isVisible) return undefined;

  return (
    <div className={styles.modalBackdrop} onClick={handleClose}>
      <div className={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        {/* Cabecera */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <Icon name="lock" />
          </div>
          <div className={styles.headerTitles}>
            <h3 className={styles.title}>Inicio de Turno de Operador</h3>
            <p className={styles.subtitle}>
              Selecciona tu perfil e ingresa tu PIN para firmar respuestas y auditoría del CRM.
            </p>
          </div>
          <button
            type="button"
            className={styles.btnClose}
            onClick={handleClose}
            aria-label="Cerrar"
          >
            <Icon name="close" />
          </button>
        </div>

        {/* Grilla visual de operadores */}
        <div className={styles.section}>
          <span className={styles.sectionLabel}>Selecciona tu perfil</span>
          <div className={styles.operatorGrid}>
            {operators.map((op) => {
              const isSelected = op.id === selectedOpId;
              return (
                <button
                  key={op.id}
                  type="button"
                  className={buildClassName(
                    styles.operatorTile,
                    isSelected && styles.operatorTileSelected,
                  )}
                  onClick={() => handleSelectOperator(op.id)}
                >
                  <div
                    className={styles.operatorTileAvatar}
                    style={`background-color: ${getAvatarColor(op.id)}`}
                  >
                    {op.name.charAt(0).toUpperCase()}
                  </div>
                  <span className={styles.operatorTileName}>
                    {op.name.split(' ')[0]}
                  </span>
                  {op.pin && (
                    <span className={styles.tileLockBadge} title="Requiere PIN">
                      🔒
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tarjeta del operador seleccionado */}
        <div className={styles.selectedOpCard}>
          <div
            className={styles.selectedOpAvatar}
            style={`background-color: ${getAvatarColor(selectedOperator.id)}`}
          >
            {selectedOperator.name.charAt(0).toUpperCase()}
          </div>
          <div className={styles.selectedOpDetails}>
            <span className={styles.selectedOpName}>{selectedOperator.name}</span>
            <span className={styles.selectedOpRole}>{selectedOperator.role}</span>
          </div>
          <span className={styles.selectedOpBadge}>
            {selectedOperator.pin ? '🔒 Requiere PIN' : '🔓 Sin PIN'}
          </span>
        </div>

        {/* Sección de PIN de 6 dígitos */}
        {selectedOperator.pin ? (
          <div className={styles.pinSection}>
            <span className={styles.sectionLabel}>
              PIN de Seguridad (6 dígitos)
            </span>
            <div className={styles.pinContainer}>
              {pinDigits.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    pinInputsRef.current[i] = el ?? undefined;
                  }}
                  className={buildClassName(
                    styles.pinDigit,
                    digit && styles.pinDigitFilled,
                    isLocked && styles.pinDigitLocked,
                  )}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  disabled={isLocked}
                  value={digit}
                  onChange={(e) => handlePinDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(i, e)}
                  placeholder="•"
                />
              ))}
            </div>

            {isLocked && (
              <div className={styles.lockoutBox}>
                ⏳ Bloqueado por seguridad: espera
                {' '}
                <strong>
                  {secondsLeft}
                  s
                </strong>
                {' '}
                para reintentar.
              </div>
            )}

            {pinError && !isLocked && (
              <div className={styles.pinErrorMsg}>
                ⚠️
                {' '}
                {pinError}
              </div>
            )}
          </div>
        ) : (
          <div className={styles.noPinNotice}>
            ℹ️ Este perfil no tiene PIN configurado. Puedes iniciar turno directamente
            o configurarlo en Configuración → Gestionar Equipo.
          </div>
        )}

        {feedback && (
          <div className={styles.feedback}>
            {feedback}
          </div>
        )}

        {/* Botones de acción */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={handleClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            disabled={isLocked || (Boolean(selectedOperator.pin) && pinDigits.join('').length < 6)}
            onClick={handleConfirm}
          >
            <Icon name="check" />
            <span>Iniciar Turno</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default memo(OperatorAuthModal);
